import React, { useState, useEffect } from "react";
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { loginUserWithOtp } from "./Apihelper/ApiHelper.js";
import AppToast from '../components/Toast/AppToast';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1B4332',
    },
    secondary: {
      main: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const glassFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    color: '#fff',
    background: 'transparent',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
    '&:hover fieldset': { borderColor: '#fff' },
    '&.Mui-focused fieldset': { borderColor: '#fff' },
  },
  '& .MuiInputBase-input': { background: 'transparent' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
};

const primaryButtonSx = {
  display: 'block',
  mx: 'auto',
  px: 4,
  py: 0.6,
  fontSize: '0.85rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '20px',
  background: 'linear-gradient(45deg, #16a34a 30%, #22c55e 90%)',
  boxShadow: '0 4px 12px rgba(22, 163, 74, 0.4)',
  '&:hover': {
    background: 'linear-gradient(45deg, #15803d 30%, #16a34a 90%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(22, 163, 74, 0.5)',
  },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.75)', background: 'rgba(22, 163, 74, 0.45)' },
};

const linkSx = {
  color: 'rgba(255,255,255,0.9)',
  textDecoration: 'none',
  fontWeight: 500,
  '&:hover': { color: '#fff', textDecoration: 'underline' },
};

export default function SignIn() {
  const navigate = useNavigate();
  const [toastmessage, settoastmessage] = useState('');
  const [toastcolor, settoastcolor] = useState('info');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // OTP step (account locked after too many wrong passwords). Holds the
  // credentials needed to re-send the login; the code itself only lives in
  // otpCode state and is never stored or logged.
  const [otpStep, setOtpStep] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendWait, setResendWait] = useState(0);

  useEffect(() => {
    // Check for remembered credentials
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberedPassword = localStorage.getItem('rememberedPassword');
    if (rememberedEmail && rememberedPassword) {
      setRememberMe(true);
      // You can set these values in your Formik initial values
      initialValues.email = rememberedEmail;
      initialValues.password = rememberedPassword;
    }
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
      setSnackbarOpen(false);
  };

  const handleForgotPasswordClick = () => {
    navigate('/forgotpassword');
};

    const initialValues = {
    email: '',
    password: '',
  };

  const handleRememberMe = (event) => {
    setRememberMe(event.target.checked);
  };
  
  const showToast = (message, color) => {
    settoastmessage(message || 'Something went wrong. Please try again.');
    settoastcolor(color);
    setSnackbarOpen(true);
  };

  // Countdown before another code may be requested.
  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const timer = setTimeout(() => setResendWait((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearTimeout(timer);
  }, [resendWait]);

  const completeLogin = (res, email, password) => {
    // Handle Remember Me
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
      localStorage.setItem('rememberedPassword', password);
    } else {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedPassword');
    }
    setLoading(false);
    setOtpStep(null);
    setOtpCode('');
    localStorage.setItem('userdata', JSON.stringify(res.admin));
    navigate('/dashboard', { state: { showSnackbar: true } });
    settoastmessage('Login Successful ! Welcome Back!');
    settoastcolor('info');
    setSnackbarOpen(true);
  };

  const enterOtpStep = (res, email, password) => {
    setOtpStep({
      email,
      password,
      contacts: res.contacts || {},
    });
    setOtpError('');
    setOtpCode('');
    setResendWait(Number(res.wait) > 0 ? Number(res.wait) : 0);
  };

  const describeContacts = (contacts) => {
    const parts = [];
    if (contacts && contacts.phone) parts.push(`WhatsApp ${contacts.phone}`);
    if (contacts && contacts.email) parts.push(contacts.email);
    if (parts.length === 0) return 'your registered WhatsApp number and email';
    return parts.join(' and ');
  };

  const handleVerifyOtp = async (event) => {
    if (event) event.preventDefault();
    if (loading || !otpStep) return;
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError('Please enter the 6-digit code.');
      return;
    }
    setLoading(true);
    setOtpError('');
    try {
      const res = await loginUserWithOtp(otpStep.email, otpStep.password, otpCode);
      if (res.code == "200") {
        completeLogin(res, otpStep.email, otpStep.password);
        return;
      }
      setOtpCode('');
      if (res.code == "423") {
        if (res.contacts) {
          setOtpStep((prev) => (prev ? { ...prev, contacts: res.contacts } : prev));
        }
        if (Number(res.wait) > 0) setResendWait(Number(res.wait));
      }
      setOtpError(res.message || 'Invalid or expired code.');
      showToast(res.message || 'Invalid or expired code.', 'error');
    } catch (error) {
      showToast(error.message, 'error');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (loading || !otpStep || resendWait > 0) return;
    setLoading(true);
    setOtpError('');
    try {
      const res = await loginUserWithOtp(otpStep.email, otpStep.password);
      if (res.code == "200") {
        completeLogin(res, otpStep.email, otpStep.password);
        return;
      }
      if (res.code == "423") {
        if (res.contacts) {
          setOtpStep((prev) => (prev ? { ...prev, contacts: res.contacts } : prev));
        }
        const wait = Number(res.wait) > 0 ? Number(res.wait) : 60;
        setResendWait(wait);
        setOtpCode('');
        showToast(res.message || 'A new code has been sent.', 'info');
      } else {
        setOtpError(res.message || 'Could not resend the code.');
        showToast(res.message, 'error');
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
    setLoading(false);
  };

  const handleBackToLogin = () => {
    if (loading) return;
    setOtpStep(null);
    setOtpCode('');
    setOtpError('');
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (loading) return;
    setLoading(true);
        setSubmitting(false);
    try {
      const res = await loginUserWithOtp(values.email, values.password);

      if (res.code == "200") {
        completeLogin(res, values.email, values.password);
      } else if (res.code == "423") {
        enterOtpStep(res, values.email, values.password);
        setLoading(false);
        showToast(res.message || 'We sent you a verification code.', 'warning');
      } else if(res.code == "500") {
        settoastmessage(res.message);
        settoastcolor('error');
        setLoading(false); 
        setSnackbarOpen(true);
      }else{
        settoastmessage(res.message);
        settoastcolor('error');
        setLoading(false); 
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error:", error);
      settoastmessage(error.message);
      setSnackbarOpen(true);
      setLoading(false); 
    } 
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${process.env.PUBLIC_URL}/whole-bg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', px: 2 }}>
          <Paper
            elevation={0}
            sx={{
              width: { xs: '88vw', sm: '420px' },
              height: { xs: '88vw', sm: '420px' },
              maxWidth: 440,
              maxHeight: 440,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              border: '1.5px solid rgba(255, 255, 255, 0.35)',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.35)',
              transition: 'all 0.3s ease-in-out',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                width: '72%',
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL || ''}/images/rectangle-9@2x.png`}
                alt="Maa Tara Tarini Multi gym"
                sx={{ width: otpStep ? 48 : 80, height: otpStep ? 48 : 80, objectFit: 'contain' }}
              />
              <Typography
                component="h1"
                variant="body1"
                sx={{ fontWeight: 700, color: '#fff', textAlign: 'center', fontSize: '0.95rem', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
              >
                Maa Tara Tarini Multi gym
              </Typography>

              {otpStep && (
                <Box component="form" onSubmit={handleVerifyOtp} noValidate sx={{ width: '100%', textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: 'rgba(255,255,255,0.92)', lineHeight: 1.35, textShadow: '0 1px 3px rgba(0,0,0,0.5)', mb: 0.5 }}
                  >
                    We sent a 6-digit code to {describeContacts(otpStep.contacts)}.
                  </Typography>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    autoFocus
                    id="otp"
                    name="otp"
                    label="6-digit code"
                    value={otpCode}
                    onChange={(e) => {
                      setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      if (otpError) setOtpError('');
                    }}
                    disabled={loading}
                    slotProps={{
                      htmlInput: {
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 6,
                        autoComplete: 'one-time-code',
                        style: { textAlign: 'center', letterSpacing: '0.4em' },
                      },
                    }}
                    sx={glassFieldSx}
                  />
                  {otpError && (
                    <Typography
                      variant="caption"
                      component="p"
                      role="alert"
                      sx={{ color: '#fecaca', fontWeight: 600, lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
                    >
                      {otpError}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || otpCode.length !== 6}
                    sx={{ ...primaryButtonSx, mt: 0.5 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify & sign in'}
                  </Button>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={handleBackToLogin}
                      disabled={loading}
                      sx={linkSx}
                    >
                      Back
                    </Link>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>|</Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={handleResendOtp}
                      disabled={loading || resendWait > 0}
                      sx={{
                        ...linkSx,
                        '&:disabled': { color: 'rgba(255,255,255,0.55)', cursor: 'default', textDecoration: 'none' },
                      }}
                    >
                      {resendWait > 0 ? `Resend code in ${resendWait}s` : 'Resend code'}
                    </Link>
                  </Box>
                </Box>
              )}

              <Box sx={{ width: '100%', display: otpStep ? 'none' : 'block' }}>
              <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                {({ isSubmitting }) => (
                  <Form style={{ width: '100%' }}>
                    <Box sx={{ width: '100%' }}>
                      <Field
                        as={TextField}
                        margin="dense"
                        size="small"
                        required
                        fullWidth
                        id="email"
                        label="Login ID"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            color: '#fff',
                            background: 'transparent',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                            '&:hover fieldset': { borderColor: '#fff' },
                            '&.Mui-focused fieldset': { borderColor: '#fff' },
                          },
                          '& .MuiInputBase-input': { background: 'transparent' },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
                          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
                            boxShadow: '0 0 0 100px transparent inset !important',
                            WebkitTextFillColor: '#fff !important',
                            caretColor: '#fff',
                            transition: 'background-color 9999s ease-in-out 0s',
                          },
                        }}
                      />
                      <Field
                        as={TextField}
                        margin="dense"
                        size="small"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            color: '#fff',
                            background: 'transparent',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                            '&:hover fieldset': { borderColor: '#fff' },
                            '&.Mui-focused fieldset': { borderColor: '#fff' },
                          },
                          '& .MuiInputBase-input': { background: 'transparent' },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
                          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
                            boxShadow: '0 0 0 100px transparent inset !important',
                            WebkitTextFillColor: '#fff !important',
                            caretColor: '#fff',
                            transition: 'background-color 9999s ease-in-out 0s',
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={handleRememberMe}
                            size="small"
                            color="primary"
                          />
                        }
                        label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>Remember me</Typography>}
                        sx={{ mt: 0.5 }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                          mt: 0.5,
                          display: 'block',
                          mx: 'auto',
                          px: 4,
                          py: 0.6,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '20px',
                          background: 'linear-gradient(45deg, #16a34a 30%, #22c55e 90%)',
                          boxShadow: '0 4px 12px rgba(22, 163, 74, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #15803d 30%, #16a34a 90%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(22, 163, 74, 0.5)',
                          },
                        }}
                      >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
                      </Button>
                      <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                        <Link
                          component="button"
                          variant="caption"
                          onClick={handleForgotPasswordClick}
                          sx={{
                            color: 'rgba(255,255,255,0.9)',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { color: '#fff', textDecoration: 'underline' },
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
              </Box>
            </Box>
          </Paper>
        </Box>
       
      <AppToast open={snackbarOpen} message={toastmessage} severity={toastcolor} onClose={handleSnackbarClose} />
    </Box>
    </ThemeProvider>
  );
}